/**
 * Professional Card Component
 * Feature: 004-restore-missing-features
 * Task: T034, T035
 *
 * Displays a professional with edit/deactivate/reactivate/remove actions
 */

import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  User,
  MoreVertical,
  Edit,
  UserX,
  UserCheck,
  Trash2,
  Shield,
  Mail,
  KeyRound,
} from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface ProfessionalCardProps {
  professional: {
    id: string
    profile_id: string
    role: 'admin' | 'professional' | 'assistant'
    specialty?: string | null
    is_active: boolean
    joined_at: string
    profiles: {
      id: string
      full_name: string
      email: string
      phone: string | null
    }
  }
  onEdit?: (id: string) => void
  onDeactivate?: (id: string) => void
  onReactivate?: (id: string) => void
  onRemove?: (id: string) => void
  onSendResetEmail?: (id: string) => void
  onGenerateTemporaryPassword?: (id: string) => void
  isSendingResetEmail?: boolean
  isGeneratingTemporaryPassword?: boolean
  currentUserId?: string
}

const roleLabels: Record<string, string> = {
  admin: 'Administrador',
  professional: 'Profissional',
  assistant: 'Assistente',
}

const roleColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800',
  professional: 'bg-blue-100 text-blue-800',
  assistant: 'bg-gray-100 text-gray-800',
}

export function ProfessionalCard({
  professional,
  onEdit,
  onDeactivate,
  onReactivate,
  onRemove,
  onSendResetEmail,
  onGenerateTemporaryPassword,
  isSendingResetEmail = false,
  isGeneratingTemporaryPassword = false,
  currentUserId,
}: ProfessionalCardProps) {
  const isCurrentUser = professional.profile_id === currentUserId
  const canManagePassword = professional.role === 'professional'
  const hasAnyAction =
    onEdit ||
    (canManagePassword && (onSendResetEmail || onGenerateTemporaryPassword)) ||
    onDeactivate ||
    onReactivate ||
    onRemove

  return (
    <Card className={!professional.is_active ? 'opacity-60' : ''}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-muted rounded-full">
              {professional.role === 'admin' ? (
                <Shield className="h-5 w-5 text-purple-600" />
              ) : (
                <User className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="font-medium">
                  {professional.profiles?.full_name ?? 'Desconhecido'}
                </p>
                {isCurrentUser && (
                  <Badge variant="outline" className="text-xs">
                    Voce
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {professional.profiles?.email ?? ''}
              </p>
              {professional.specialty && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {professional.specialty}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Badge
                  className={
                    roleColors[professional.role] ?? roleColors.assistant
                  }
                >
                  {roleLabels[professional.role] ?? professional.role}
                </Badge>
                <Badge
                  className={
                    professional.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }
                >
                  {professional.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Desde{' '}
                {format(new Date(professional.joined_at), "d 'de' MMMM, yyyy", {
                  locale: ptBR,
                })}
              </p>
            </div>
          </div>

          {!isCurrentUser && hasAnyAction && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(professional.id)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {onEdit &&
                  canManagePassword &&
                  (onSendResetEmail || onGenerateTemporaryPassword) && (
                    <DropdownMenuSeparator />
                  )}
                {canManagePassword && onSendResetEmail && (
                  <DropdownMenuItem
                    onClick={() => onSendResetEmail(professional.id)}
                    disabled={isSendingResetEmail}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Enviar reset por email
                  </DropdownMenuItem>
                )}
                {canManagePassword && onGenerateTemporaryPassword && (
                  <DropdownMenuItem
                    onClick={() => onGenerateTemporaryPassword(professional.id)}
                    disabled={isGeneratingTemporaryPassword}
                    className="text-amber-600 focus:text-amber-600"
                  >
                    <KeyRound className="mr-2 h-4 w-4" />
                    Gerar senha temporaria
                  </DropdownMenuItem>
                )}
                {/* Reactivate action - only for inactive professionals */}
                {onReactivate && !professional.is_active && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onReactivate(professional.id)}
                    >
                      <UserCheck className="mr-2 h-4 w-4" />
                      Reativar
                    </DropdownMenuItem>
                  </>
                )}
                {/* Deactivate action - only for active professionals */}
                {onDeactivate && professional.is_active && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDeactivate(professional.id)}
                      className="text-orange-600 focus:text-orange-600"
                    >
                      <UserX className="mr-2 h-4 w-4" />
                      Desativar
                    </DropdownMenuItem>
                  </>
                )}
                {/* Remove action - permanently removes from organization */}
                {onRemove && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onRemove(professional.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remover
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
